import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const HighRiskCorridorOversight = () => {
  const [selectedCorridor, setSelectedCorridor] = useState(null);

  // Mock corridor data
  const corridors = [
    {
      id: 'COR-001',
      sender: 'United States',
      receiver: 'Somalia',
      volume24h: '$487,200',
      volumeChange: '+43%',
      transactionCount: 156,
      riskScore: 87,
      riskLevel: 'critical',
      avgTransactionSize: '$3,123',
      flaggedCount: 23,
      alert: {
        triggered: true,
        message: 'Somalia corridor jumped 43% in last 24 hours → auto-review triggered'
      },
      recentActivity: [
        { time: '2h ago', amount: '$12,500', customer: 'CUST-456' },
        { time: '4h ago', amount: '$8,900', customer: 'CUST-789' },
        { time: '6h ago', amount: '$15,200', customer: 'CUST-123' }
      ]
    },
    {
      id: 'COR-002',
      sender: 'United Kingdom',
      receiver: 'Afghanistan',
      volume24h: '$234,800',
      volumeChange: '+18%',
      transactionCount: 89,
      riskScore: 78,
      riskLevel: 'high',
      avgTransactionSize: '$2,639',
      flaggedCount: 12,
      alert: {
        triggered: false,
        message: null
      },
      recentActivity: [
        { time: '1h ago', amount: '$5,600', customer: 'CUST-234' },
        { time: '3h ago', amount: '$9,200', customer: 'CUST-567' }
      ]
    },
    {
      id: 'COR-003',
      sender: 'United States',
      receiver: 'Syria',
      volume24h: '$156,300',
      volumeChange: '+29%',
      transactionCount: 67,
      riskScore: 82,
      riskLevel: 'critical',
      avgTransactionSize: '$2,333',
      flaggedCount: 18,
      alert: {
        triggered: true,
        message: 'Unusual spike in Syria corridor - Enhanced monitoring active'
      },
      recentActivity: [
        { time: '30m ago', amount: '$7,800', customer: 'CUST-890' },
        { time: '2h ago', amount: '$11,400', customer: 'CUST-345' }
      ]
    },
    {
      id: 'COR-004',
      sender: 'Canada',
      receiver: 'Yemen',
      volume24h: '$98,700',
      volumeChange: '+12%',
      transactionCount: 45,
      riskScore: 73,
      riskLevel: 'high',
      avgTransactionSize: '$2,193',
      flaggedCount: 8,
      alert: {
        triggered: false,
        message: null
      },
      recentActivity: [
        { time: '5h ago', amount: '$4,200', customer: 'CUST-678' }
      ]
    }
  ];

  const getRiskBadge = (level) => {
    const config = {
      critical: { bg: 'bg-error/10', text: 'text-error', label: 'Critical Risk' },
      high: { bg: 'bg-warning/10', text: 'text-warning', label: 'High Risk' },
      medium: { bg: 'bg-accent/10', text: 'text-accent', label: 'Medium Risk' },
      low: { bg: 'bg-success/10', text: 'text-success', label: 'Low Risk' }
    };
    const badge = config?.[level] || config?.medium;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${badge?.bg} ${badge?.text}`}>
        {badge?.label}
      </span>
    );
  };

  const getRiskColor = (score) => {
    if (score >= 80) return 'text-error';
    if (score >= 60) return 'text-warning';
    if (score >= 40) return 'text-accent';
    return 'text-success';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-foreground">High-Risk Corridor Oversight</h2>
            <p className="text-muted-foreground mt-1">
              Monitor transaction volumes and risk exposure by geographic corridor
            </p>
          </div>
          <button className="flex items-center space-x-2 px-4 py-2 border border-border rounded-lg hover:bg-muted/50 transition-colors">
            <Icon name="Download" size={20} />
            <span>Export Report</span>
          </button>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-muted/30 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Corridors</p>
                <p className="text-2xl font-bold text-foreground mt-1">{corridors?.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="MapPin" size={24} className="text-primary" />
              </div>
            </div>
          </div>
          <div className="bg-muted/30 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">24h Volume</p>
                <p className="text-2xl font-bold text-foreground mt-1">$976.9K</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                <Icon name="DollarSign" size={24} className="text-success" />
              </div>
            </div>
          </div>
          <div className="bg-muted/30 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Alerts</p>
                <p className="text-2xl font-bold text-foreground mt-1">
                  {corridors?.filter(c => c?.alert?.triggered)?.length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
                <Icon name="AlertTriangle" size={24} className="text-error" />
              </div>
            </div>
          </div>
          <div className="bg-muted/30 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Risk Score</p>
                <p className="text-2xl font-bold text-foreground mt-1">80.0</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
                <Icon name="Activity" size={24} className="text-warning" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Corridors Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {corridors?.map((corridor) => (
          <div
            key={corridor?.id}
            className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
          >
            {/* Alert Banner */}
            {corridor?.alert?.triggered && (
              <div className="bg-error/10 border-b border-error/20 p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertTriangle" size={20} className="text-error mt-0.5" />
                  <p className="text-sm text-error font-medium">{corridor?.alert?.message}</p>
                </div>
              </div>
            )}

            <div className="p-6">
              {/* Corridor Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-semibold text-foreground">
                      {corridor?.sender} → {corridor?.receiver}
                    </h3>
                  </div>
                  {getRiskBadge(corridor?.riskLevel)}
                </div>
                <div className={`text-3xl font-bold ${getRiskColor(corridor?.riskScore)}`}>
                  {corridor?.riskScore}
                </div>
              </div>

              {/* Volume Stats */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-muted-foreground">24h Volume</p>
                  <p className="text-xl font-bold text-foreground">{corridor?.volume24h}</p>
                  <span className={`text-xs font-medium ${
                    corridor?.volumeChange?.startsWith('+') ? 'text-error' : 'text-success'
                  }`}>
                    {corridor?.volumeChange}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Transactions</p>
                  <p className="text-xl font-bold text-foreground">{corridor?.transactionCount}</p>
                  <span className="text-xs text-muted-foreground">
                    Avg: {corridor?.avgTransactionSize}
                  </span>
                </div>
              </div>

              {/* Flagged Count */}
              <div className="bg-muted/30 p-3 rounded-lg mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Flagged Transactions</span>
                  <span className="text-lg font-bold text-error">{corridor?.flaggedCount}</span>
                </div>
              </div>

              {/* Recent Activity */}
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Recent Activity</h4>
                <div className="space-y-2">
                  {corridor?.recentActivity?.slice(0, 3)?.map((activity, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{activity?.time}</span>
                      <span className="font-medium text-foreground">{activity?.amount}</span>
                      <span className="text-xs text-muted-foreground">{activity?.customer}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-border">
                <button
                  onClick={() => setSelectedCorridor(corridor)}
                  className="flex-1 px-4 py-2 border border-border rounded-lg hover:bg-muted/50 transition-colors text-sm font-medium"
                >
                  View Details
                </button>
                <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">
                  Review Now
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HighRiskCorridorOversight;